import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { supabaseService } from '../lib/supabaseService';
import { logAction, getCurrentAdmin } from '../services/logger';
import { dispatchWorkflow, hasGitHubToken } from '../services/github';
import { buildExpoMessages, sendExpoBatch } from '../services/expoPush';
import {
  KNOWN_TEAM_KEYS,
  listBarsForTargeting,
  listKnownCities,
  countMatching,
  resolveTokens,
  type TargetType,
  type BarOption,
} from '../services/targeting';
import {
  Bell,
  Send,
  Smartphone,
  Clock,
  AlertTriangle,
  Eye,
  Target,
  Link as LinkIcon,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast from 'react-hot-toast';

const WORKFLOW_FILE = 'notifications.yml';
const TITLE_MAX = 50;
const BODY_MAX = 150;

interface Stats {
  activeTokens: number;
  enabledNotifs: number;
  iosCount: number;
  androidCount: number;
  lastSendAt: string | null;
  lastSendBy: string | null;
}

interface HistoryLog {
  id: string;
  admin_email: string;
  action: string;
  description: string;
  new_value: any;
  created_at: string;
}

const TARGET_LABELS: Record<TargetType, string> = {
  all: 'Tous les utilisateurs',
  city: 'Utilisateurs dans la ville',
  team: 'Utilisateurs avec équipe favorite',
  bar: 'Utilisateurs avec bar favori',
};

function targetDescription(type: TargetType, value: string | null, barLookup: Record<string, string>): string {
  if (type === 'all') return 'Tous les utilisateurs';
  if (type === 'city') return `Ville = ${value || '—'}`;
  if (type === 'team') {
    const team = KNOWN_TEAM_KEYS.find(k => k.key === value);
    return `Équipe favorite = ${team?.label || value || '—'}`;
  }
  if (type === 'bar') {
    return `Bar favori = ${barLookup[value || ''] || value || '—'}`;
  }
  return '—';
}

export function NotificationsPage() {
  // ── Stats ──
  const [stats, setStats] = useState<Stats>({
    activeTokens: 0,
    enabledNotifs: 0,
    iosCount: 0,
    androidCount: 0,
    lastSendAt: null,
    lastSendBy: null,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryLog[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // ── Formulaire ──
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetType, setTargetType] = useState<TargetType>('all');
  const [targetValue, setTargetValue] = useState<string>('Lille');
  const [deepLink, setDeepLink] = useState('');
  const [sending, setSending] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ── Options pour sélecteurs ──
  const [cities, setCities] = useState<string[]>(['Lille']);
  const [bars, setBars] = useState<BarOption[]>([]);

  const barLookup = useMemo(() => {
    const map: Record<string, string> = {};
    for (const b of bars) map[b.id] = b.name;
    return map;
  }, [bars]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const [tokensRes, enabledRes, platformsRes, lastSendRes] = await Promise.all([
        supabaseService
          .from('profiles')
          .select('id', { count: 'exact', head: true })
          .not('push_token', 'is', null),
        supabaseService
          .from('profiles')
          .select('id', { count: 'exact', head: true })
          .eq('notifications_enabled', true)
          .not('push_token', 'is', null),
        supabaseService.from('push_tokens').select('platform'),
        supabase
          .from('admin_logs')
          .select('created_at, admin_email')
          .in('action', ['send_notification', 'push_notification_sent'])
          .order('created_at', { ascending: false })
          .limit(1),
      ]);

      const platformRows = (platformsRes.data || []) as Array<{ platform: string | null }>;
      const iosCount = platformRows.filter(r => r.platform === 'ios').length;
      const androidCount = platformRows.filter(r => r.platform === 'android').length;

      const lastLog = (lastSendRes.data || [])[0];

      setStats({
        activeTokens: tokensRes.count || 0,
        enabledNotifs: enabledRes.count || 0,
        iosCount,
        androidCount,
        lastSendAt: lastLog?.created_at || null,
        lastSendBy: lastLog?.admin_email || null,
      });
    } catch (err: any) {
      console.error('Load notification stats error:', err);
      setStatsError(err?.message || 'Erreur de chargement');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_logs')
        .select('id, admin_email, action, description, new_value, created_at')
        .in('action', ['send_notification', 'push_notification_sent'])
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      setHistory((data || []) as HistoryLog[]);
    } catch (err) {
      console.error('Load notification history error:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadTargetingOptions = useCallback(async () => {
    try {
      const [cityList, barList] = await Promise.all([listKnownCities(), listBarsForTargeting()]);
      setCities(cityList.length > 0 ? cityList : ['Lille']);
      setBars(barList);
    } catch (err) {
      console.error('Load targeting options error:', err);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadHistory();
    loadTargetingOptions();
  }, [loadStats, loadHistory, loadTargetingOptions]);

  // Recalcul du count quand la cible change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      try {
        const value =
          targetType === 'all'
            ? undefined
            : targetType === 'city'
            ? targetValue
            : targetType === 'team'
            ? targetValue
            : targetType === 'bar'
            ? targetValue
            : undefined;
        const count = await countMatching({ type: targetType, value });
        if (!cancelled) setPreviewCount(count);
      } catch (err) {
        console.error('Count matching error:', err);
        if (!cancelled) setPreviewCount(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetType, targetValue]);

  const resetForm = () => {
    setTitle('');
    setBody('');
    setTargetType('all');
    setTargetValue('Lille');
    setDeepLink('');
  };

  const validateForm = (): string | null => {
    if (!title.trim()) return 'Titre obligatoire';
    if (title.length > TITLE_MAX) return `Titre trop long (${title.length}/${TITLE_MAX})`;
    if (!body.trim()) return 'Corps du message obligatoire';
    if (body.length > BODY_MAX) return `Corps trop long (${body.length}/${BODY_MAX})`;
    if (targetType !== 'all' && !targetValue) return 'Sélectionne une cible';
    if (deepLink && !deepLink.startsWith('onpitch://')) {
      return 'Deep link doit commencer par onpitch://';
    }
    return null;
  };

  const handleSend = async () => {
    const err = validateForm();
    if (err) {
      toast.error(err);
      return;
    }
    if (previewCount === 0) {
      toast.error('Aucun destinataire ne correspond à cette cible');
      return;
    }

    const cible = targetDescription(targetType, targetValue, barLookup);
    const confirmMsg = `Envoyer à ${previewCount ?? '?'} utilisateur${(previewCount ?? 0) > 1 ? 's' : ''} ?\n\n${title}\n${body}\n\nCible : ${cible}${deepLink ? `\nLien : ${deepLink}` : ''}`;
    if (!window.confirm(confirmMsg)) return;

    setSending(true);
    const sentMethod = hasGitHubToken() ? 'github' : 'direct';
    let sentCount = 0;
    let sendError: string | null = null;

    try {
      if (hasGitHubToken()) {
        // Route 1 — GitHub workflow dispatch
        await dispatchWorkflow(WORKFLOW_FILE, {
          mode: 'custom',
          title: title.trim(),
          body: body.trim(),
          target_type: targetType,
          target_value: targetType === 'all' ? '' : targetValue,
          deep_link: deepLink.trim(),
          custom_message: '',
        });
        sentCount = previewCount ?? 0;
      } else {
        // Route 2 — Fallback Expo Push direct depuis le navigateur
        const tokens = await resolveTokens({
          type: targetType,
          value: targetType === 'all' ? undefined : targetValue,
        });
        if (tokens.length === 0) {
          toast.error('Aucun token à notifier');
          setSending(false);
          return;
        }
        const data: Record<string, unknown> = { source: 'admin_custom' };
        if (deepLink.trim()) data.url = deepLink.trim();
        const messages = buildExpoMessages(tokens, {
          title: title.trim(),
          body: body.trim(),
          data,
          sound: 'default',
          badge: 1,
        });
        const result = await sendExpoBatch(messages);
        sentCount = result.delivered;
        if (result.errors.length > 0) {
          sendError = result.errors.slice(0, 3).join(' · ');
          console.warn('Expo push errors:', result.errors);
        }
      }

      const admin = getCurrentAdmin();
      await logAction(
        'push_notification_sent',
        'notification',
        null,
        null,
        {
          title: title.trim(),
          body: body.trim(),
          target_type: targetType,
          target_value: targetType === 'all' ? null : targetValue,
          deep_link: deepLink.trim() || null,
          sent_count: sentCount,
          method: sentMethod,
        },
        `${admin?.name || 'Admin'} a envoyé « ${title.trim()} » à ${sentCount} utilisateur${sentCount > 1 ? 's' : ''} (${cible})`,
      );

      if (sendError) {
        toast(
          `Envoyé à ${sentCount} users — erreurs partielles : ${sendError}`,
          { icon: '⚠️', duration: 6000 },
        );
      } else if (sentMethod === 'github') {
        toast.success(`Workflow déclenché — ${sentCount} destinataires visés`);
      } else {
        toast.success(`Notification envoyée à ${sentCount} utilisateur${sentCount > 1 ? 's' : ''}`);
      }

      resetForm();
      // Refresh après un court délai — workflow met qq secondes à logger
      setTimeout(() => {
        loadStats();
        loadHistory();
      }, 2500);
    } catch (err: any) {
      console.error('Send notification error:', err);
      toast.error(err?.message || 'Erreur envoi');
    } finally {
      setSending(false);
    }
  };

  const lastSendLabel = stats.lastSendAt
    ? formatDistanceToNow(new Date(stats.lastSendAt), { addSuffix: true, locale: fr })
    : '—';

  const cibleLabel = targetDescription(targetType, targetValue, barLookup);

  return (
    <div>
      <h2
        style={{
          fontSize: '20px',
          fontWeight: 700,
          margin: '0 0 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <Bell size={20} style={{ color: 'var(--accent)' }} />
        Notifications push
      </h2>

      {/* ── Stats ── */}
      <div className="stats-grid">
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Tokens actifs</div>
              <div className="stat-value">{statsLoading ? '…' : stats.activeTokens}</div>
              <div className="stat-sub">profils avec push_token</div>
            </div>
            <Smartphone size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Notifs activées</div>
              <div className="stat-value" style={{ color: 'var(--green)' }}>
                {statsLoading ? '…' : stats.enabledNotifs}
              </div>
              <div className="stat-sub">prêts à recevoir</div>
            </div>
            <Bell size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Dernier envoi</div>
              <div
                className="stat-value"
                style={{ fontSize: stats.lastSendAt ? '18px' : '26px' }}
              >
                {statsLoading ? '…' : lastSendLabel}
              </div>
              <div className="stat-sub">
                {stats.lastSendBy ? `par ${stats.lastSendBy.split('@')[0]}` : 'aucun envoi manuel'}
              </div>
            </div>
            <Clock size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
      </div>

      {statsError && (
        <div
          className="card"
          style={{
            marginBottom: '16px',
            borderColor: 'var(--red-dim)',
            background: 'var(--red-dim)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--red)',
              fontSize: '13px',
            }}
          >
            <AlertTriangle size={16} />
            Impossible de charger les stats : {statsError}
          </div>
        </div>
      )}

      {/* ── Formulaire + Aperçu ── */}
      <div
        className="dashboard-2col"
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '16px',
          marginBottom: '16px',
        }}
      >
        <div className="card">
          <h3 className="card-title">Envoyer une notification personnalisée</h3>

          {!hasGitHubToken() && (
            <div
              style={{
                padding: '10px 12px',
                marginBottom: '14px',
                background: 'var(--orange-dim)',
                border: '1px solid rgba(198,122,0,0.25)',
                borderRadius: '8px',
                color: 'var(--orange)',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <AlertTriangle size={14} />
              <code style={{ fontFamily: 'monospace' }}>REACT_APP_GITHUB_TOKEN</code> absent —
              envoi direct via Expo Push (fallback activé)
            </div>
          )}

          <div className="form-field">
            <label style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Titre</span>
              <span
                style={{
                  fontSize: '11px',
                  color: title.length > TITLE_MAX ? 'var(--red)' : 'var(--text-dim)',
                }}
              >
                {title.length}/{TITLE_MAX}
              </span>
            </label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value.slice(0, TITLE_MAX + 10))}
              maxLength={TITLE_MAX + 10}
              placeholder="Ex : 🏟️ Ce soir à Lille"
              disabled={sending}
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-field">
            <label style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Corps du message</span>
              <span
                style={{
                  fontSize: '11px',
                  color: body.length > BODY_MAX ? 'var(--red)' : 'var(--text-dim)',
                }}
              >
                {body.length}/{BODY_MAX}
              </span>
            </label>
            <textarea
              rows={3}
              value={body}
              onChange={e => setBody(e.target.value.slice(0, BODY_MAX + 20))}
              placeholder="Ex : 5 matchs diffusés ce soir. Trouve ton bar !"
              disabled={sending}
              style={{ fontSize: '13px', resize: 'vertical', width: '100%' }}
            />
          </div>

          <div className="form-field">
            <label>
              <Target size={13} style={{ display: 'inline', marginRight: '4px' }} />
              Cible
            </label>
            <select
              className="filter-select"
              value={targetType}
              onChange={e => {
                const t = e.target.value as TargetType;
                setTargetType(t);
                if (t === 'city') setTargetValue(cities[0] || 'Lille');
                else if (t === 'team') setTargetValue(KNOWN_TEAM_KEYS[0].key);
                else if (t === 'bar') setTargetValue(bars[0]?.id || '');
                else setTargetValue('');
              }}
              disabled={sending}
              style={{ width: '100%' }}
            >
              {(Object.keys(TARGET_LABELS) as TargetType[]).map(t => (
                <option key={t} value={t}>
                  {TARGET_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          {targetType === 'city' && (
            <div className="form-field">
              <label>Ville</label>
              <select
                className="filter-select"
                value={targetValue}
                onChange={e => setTargetValue(e.target.value)}
                disabled={sending}
                style={{ width: '100%' }}
              >
                {cities.map(c => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          )}

          {targetType === 'team' && (
            <div className="form-field">
              <label>Équipe favorite</label>
              <select
                className="filter-select"
                value={targetValue}
                onChange={e => setTargetValue(e.target.value)}
                disabled={sending}
                style={{ width: '100%' }}
              >
                {KNOWN_TEAM_KEYS.map(k => (
                  <option key={k.key} value={k.key}>
                    {k.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {targetType === 'bar' && (
            <div className="form-field">
              <label>Bar favori</label>
              <select
                className="filter-select"
                value={targetValue}
                onChange={e => setTargetValue(e.target.value)}
                disabled={sending || bars.length === 0}
                style={{ width: '100%' }}
              >
                {bars.length === 0 ? (
                  <option value="">Aucun bar disponible</option>
                ) : (
                  bars.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))
                )}
              </select>
            </div>
          )}

          <div className="form-field">
            <label>
              <LinkIcon size={13} style={{ display: 'inline', marginRight: '4px' }} />
              Deep link (optionnel)
            </label>
            <input
              type="text"
              value={deepLink}
              onChange={e => setDeepLink(e.target.value)}
              placeholder="onpitch://match/123 ou onpitch://bar/abc"
              disabled={sending}
              style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }}
            />
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginTop: '12px',
              flexWrap: 'wrap',
            }}
          >
            <button
              className="btn btn-primary"
              onClick={handleSend}
              disabled={sending || !title.trim() || !body.trim()}
            >
              <Send size={14} />
              {sending ? 'Envoi…' : 'Envoyer'}
            </button>
            <span
              style={{
                fontSize: '12px',
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Target size={12} />
              {previewLoading ? (
                '…'
              ) : (
                <>
                  <strong style={{ color: 'var(--accent)' }}>{previewCount ?? '?'}</strong>{' '}
                  destinataire{(previewCount ?? 0) > 1 ? 's' : ''} · {cibleLabel}
                </>
              )}
            </span>
          </div>
        </div>

        {/* Aperçu */}
        <div className="card">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Eye size={14} />
            Aperçu
          </h3>
          <div
            style={{
              background: 'linear-gradient(135deg, #f5f5f7 0%, #e8e8ec 100%)',
              border: '1px solid var(--border)',
              borderRadius: '14px',
              padding: '14px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              marginBottom: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '7px',
                  background: '#1B5E20',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '11px',
                  flexShrink: 0,
                }}
              >
                OP
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: '2px',
                  }}
                >
                  <strong style={{ fontSize: '12px', color: '#111' }}>ONPITCH</strong>
                  <span style={{ fontSize: '10px', color: '#666' }}>à l'instant</span>
                </div>
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#111',
                    marginBottom: '2px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical' as const,
                  }}
                >
                  {title || 'Titre de la notification'}
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#333',
                    lineHeight: '1.4',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical' as const,
                  }}
                >
                  {body || 'Corps du message…'}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>iOS</span>
              <strong>{stats.iosCount}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Android</span>
              <strong>{stats.androidCount}</strong>
            </div>
            <div style={{ marginTop: '4px', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
              Mode :{' '}
              <strong style={{ color: hasGitHubToken() ? 'var(--green)' : 'var(--orange)' }}>
                {hasGitHubToken() ? 'Workflow GitHub' : 'Expo direct (fallback)'}
              </strong>
            </div>
          </div>
        </div>
      </div>

      {/* ── Historique ── */}
      <div className="card">
        <h3 className="card-title">Historique des envois</h3>
        {historyLoading ? (
          <p className="muted">Chargement…</p>
        ) : history.length === 0 ? (
          <p className="muted">Aucun envoi enregistré</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Titre</th>
                <th>Cible</th>
                <th>Reçus</th>
                <th>Admin</th>
              </tr>
            </thead>
            <tbody>
              {history.map(log => {
                const nv = log.new_value || {};
                const date = format(new Date(log.created_at), "dd/MM HH'h'mm", { locale: fr });
                const titleCell = nv.title || nv.mode || '—';
                const cibleCell = nv.target_type
                  ? targetDescription(nv.target_type as TargetType, nv.target_value ?? null, barLookup)
                  : nv.mode
                  ? `Legacy · ${nv.mode}`
                  : '—';
                const sentCell = typeof nv.sent_count === 'number' ? nv.sent_count : '—';
                return (
                  <tr key={log.id}>
                    <td style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{date}</td>
                    <td style={{ fontSize: '12px', fontWeight: 600 }}>{titleCell}</td>
                    <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{cibleCell}</td>
                    <td style={{ fontSize: '12px' }}>{sentCell}</td>
                    <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {log.admin_email.split('@')[0]}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
